import { useState } from "react";
import { Package, RefreshCw, Check, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { NumericInput } from "../ui/numeric-input";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { useAuth } from "../../context/AuthContext";
import { jobsService } from "../../services/api";

// Shared components
import EstimateCard from "./EstimateCard";
import InvoiceCard from "./InvoiceCard";

const MaterialsTab = ({
  job,
  suppliers,
  canManageJobs,
  onSaveMaterialEstimates,
  onAddInvoice,
  onEditInvoice,
  onDeleteInvoice,
  onToggleInvoicePaid,
  onPopulateFromBidSheet,
  hasBidSheet,
  onRefreshJob,
}) => {
  const { getAuthHeaders } = useAuth();
  
  // Materials tracking state
  const [editingQuantities, setEditingQuantities] = useState({});
  const [savingTracking, setSavingTracking] = useState(false);
  const [initializingTracking, setInitializingTracking] = useState(false);

  // Materials tracking from job
  const materialsTracking = job.materials_tracking || [];
  const isContracted = job.contract_received_date || ["active", "precon", "complete"].includes(job.status);
  const hasTracking = materialsTracking.length > 0;

  // Initialize materials tracking from bid sheets
  const handleInitTracking = async () => {
    setInitializingTracking(true);
    try {
      await jobsService.initMaterialsTracking(job.id);
      toast.success("Materials tracking initialized from bid sheets");
      if (onRefreshJob) onRefreshJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initialize tracking");
    } finally {
      setInitializingTracking(false);
    }
  };

  // Save tracking updates
  const handleSaveTracking = async () => {
    const updates = Object.entries(editingQuantities)
      .filter(([_, qty]) => qty !== "" && qty !== 0)
      .map(([id, qty]) => ({
        id,
        installed_today: parseFloat(qty) || 0
      }));

    if (updates.length === 0) {
      toast.info("No quantities to update");
      return;
    }

    setSavingTracking(true);
    try {
      await jobsService.updateMaterialsTracking(job.id, updates);
      toast.success("Materials tracking updated");
      setEditingQuantities({});
      if (onRefreshJob) onRefreshJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update tracking");
    } finally {
      setSavingTracking(false);
    }
  };

  const handleClearEdits = () => setEditingQuantities({});

  const hasPendingEdits = Object.keys(editingQuantities).some(id => 
    editingQuantities[id] !== "" && editingQuantities[id] !== 0
  );

  return (
    <div className="space-y-6">
      {/* Material Estimates - using shared EstimateCard */}
      <EstimateCard
        title="Material"
        estimates={job.material_estimates || []}
        totalEstimate={job.total_material_estimate || 0}
        canManage={canManageJobs}
        onSave={onSaveMaterialEstimates}
        onPopulateFromBidSheet={onPopulateFromBidSheet}
        hasBidSheet={hasBidSheet}
        nameField="material_name"
        idField="material_id"
        testIdPrefix="material-estimate"
      />

      {/* Materials Installed Tracking */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="w-5 h-5" />
            Materials Installed
          </CardTitle>
          <div className="flex items-center gap-2">
            {canManageJobs && !hasTracking && isContracted && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleInitTracking}
                disabled={initializingTracking}
                data-testid="init-materials-tracking-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${initializingTracking ? "animate-spin" : ""}`} />
                Load from Bid Sheets
              </Button>
            )}
            {canManageJobs && hasTracking && hasPendingEdits && (
              <>
                <Button size="sm" variant="outline" onClick={handleClearEdits} data-testid="clear-tracking-edits-btn">
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
                <Button size="sm" onClick={handleSaveTracking} disabled={savingTracking} data-testid="save-materials-tracking-btn">
                  <Check className="w-4 h-4 mr-2" />
                  {savingTracking ? "Saving..." : "Save"}
                </Button>
              </>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!isContracted ? (
            <p className="text-muted-foreground text-center py-6">
              Materials tracking available after job is contracted
            </p>
          ) : !hasTracking ? (
            <div className="text-center py-6">
              <p className="text-muted-foreground mb-2">No materials tracking set up</p>
              {canManageJobs && (
                <p className="text-sm text-muted-foreground">
                  Click &quot;Load from Bid Sheets&quot; to populate materials from accepted bid sheets
                </p>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Material</TableHead>
                    <TableHead className="text-right">Est. Qty</TableHead>
                    <TableHead className="text-right w-32">Installed Today</TableHead>
                    <TableHead className="text-right">Installed Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materialsTracking.map((mat) => {
                    const installedToday = editingQuantities[mat.id] ?? "";
                    const installedTotal = mat.installed_total || 0;
                    const estimatedQty = mat.estimated_qty || 0;
                    const progressPercent = estimatedQty > 0 ? Math.round((installedTotal / estimatedQty) * 100) : 0;
                    const isOver = installedTotal > estimatedQty;
                    
                    return (
                      <TableRow key={mat.id} className="table-row">
                        <TableCell>
                          <div>
                            <span className="font-medium">{mat.material_name}</span>
                            {mat.source_sheet_name && (
                              <p className="text-xs text-muted-foreground">{mat.source_sheet_name}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {estimatedQty.toLocaleString()} {mat.unit}
                        </TableCell>
                        <TableCell className="text-right">
                          {canManageJobs ? (
                            <NumericInput
                              decimals={2}
                              allowNegative={true}
                              value={installedToday}
                              onChange={(e) => setEditingQuantities(prev => ({
                                ...prev,
                                [mat.id]: e.target.value
                              }))}
                              placeholder="0"
                              className="w-24 text-right ml-auto"
                              data-testid={`installed-today-${mat.id}`}
                            />
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className={`font-mono font-medium ${isOver ? "text-orange-600" : ""}`}>
                            {installedTotal.toLocaleString()} {mat.unit}
                          </div>
                          <div className={`text-xs ${progressPercent >= 100 ? "text-green-600" : "text-muted-foreground"}`}>
                            {progressPercent}%
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <p className="text-xs text-muted-foreground text-center mt-3">
                Enter quantities installed today and click Save. Use negative numbers for corrections.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MaterialsTab;
